import type { AttachmentKind } from '../api/types';

/**
 * `Modal`'s attachments state is a plain `File[]` (see `Attachments.tsx`) shared by both
 * user-picked files and a confirmed screenshot. Task 9's submit assembly needs to tell those
 * apart — a screenshot uploads with `kind: 'SCREENSHOT'`, everything else with `'ATTACHMENT'`
 * (see `../attachments/upload.ts`) — without `Modal` having to thread a parallel `File ->
 * kind` map through its state and every consumer of `attachments`.
 *
 * Instead, a screenshot `File` is tagged with a non-enumerable expando property at the point
 * `Modal` creates it (`tagAttachmentKind`); `attachmentKind` reads it back, defaulting to
 * `'ATTACHMENT'` for any file that was never tagged (i.e. everything the user picked via the
 * file input). Plain `File`/`Blob` instances are ordinary JS objects in both real browsers and
 * jsdom, so adding an expando property to one is legal and survives as long as the same
 * object reference does (array copies, `[...files]`, etc. all preserve it).
 */
const KIND_TAG = Symbol.for('feedback-manager.attachment-kind');

export function tagAttachmentKind(file: File, kind: AttachmentKind): File {
  Object.defineProperty(file, KIND_TAG, { value: kind, enumerable: false, configurable: true });
  return file;
}

export function attachmentKind(file: File): AttachmentKind {
  return (file as unknown as Record<symbol, AttachmentKind | undefined>)[KIND_TAG] ?? 'ATTACHMENT';
}
